import { create } from 'zustand';
import {
  DocumentType,
  PDFAnalysisResult,
  ExtractedPedigreeData,
  ExtractedMicrochipData,
  ExtractedGeneticTestData,
  ExtractedUnifiedData,
} from '@/types/pdfAnalysis';
import { pdfAnalysisService } from '@/services/ai/pdfAnalysisService';

/**
 * PDF解析ストアの状態型定義
 */
interface PDFAnalysisState {
  // 状態
  /** ローディング状態 */
  loading: boolean;
  /** エラーメッセージ */
  error: string | null;
  /** 解析結果 */
  result: PDFAnalysisResult | null;
  /** 選択中のドキュメントタイプ */
  selectedDocumentType: DocumentType;
  /** 選択中のファイル */
  selectedFile: File | null;
  /** プレビューモーダルの表示状態 */
  isPreviewOpen: boolean;

  // アクション
  /** ファイルを設定 */
  setFile: (file: File | null) => void;
  /** PDFを解析 */
  analyze: (userId: string) => Promise<void>;
  /** 解析結果をクリア */
  clearResult: () => void;
  /** プレビューモーダルを開く */
  openPreview: () => void;
  /** プレビューモーダルを閉じる */
  closePreview: () => void;
  /** エラーをセット */
  setError: (error: string | null) => void;
  /** 全てリセット */
  reset: () => void;
}

/**
 * PDF解析ストア
 */
export const usePDFAnalysisStore = create<PDFAnalysisState>((set, get) => ({
  // 初期状態
  loading: false,
  error: null,
  result: null,
  selectedDocumentType: 'auto',
  selectedFile: null,
  isPreviewOpen: false,

  /**
   * ファイルを設定
   */
  setFile: (file) => {
    set({ selectedFile: file, result: null, error: null });
  },

  /**
   * PDFを解析
   */
  analyze: async (userId) => {
    const { selectedFile, selectedDocumentType } = get();

    if (!selectedFile) {
      set({ error: 'ファイルが選択されていません' });
      return;
    }

    set({ loading: true, error: null, result: null });

    try {
      const result = await pdfAnalysisService.analyzePDF(
        selectedFile,
        selectedDocumentType,
        userId
      );

      if (result.success) {
        set({
          result,
          loading: false,
          isPreviewOpen: true,
        });
      } else {
        set({
          result: null,
          error: result.error || '解析に失敗しました',
          loading: false,
        });
      }
    } catch (error) {
      console.error('PDF解析エラー:', error);
      set({
        result: null,
        error: error instanceof Error ? error.message : '解析中にエラーが発生しました',
        loading: false,
      });
    }
  },

  /**
   * 解析結果をクリア
   */
  clearResult: () => {
    set({ result: null, error: null });
  },

  /**
   * プレビューモーダルを開く
   */
  openPreview: () => {
    set({ isPreviewOpen: true });
  },

  /**
   * プレビューモーダルを閉じる
   */
  closePreview: () => {
    set({ isPreviewOpen: false });
  },

  /**
   * エラーをセット
   */
  setError: (error) => {
    set({ error });
  },

  /**
   * 全てリセット
   */
  reset: () => {
    set({
      loading: false,
      error: null,
      result: null,
      selectedDocumentType: 'auto',
      selectedFile: null,
      isPreviewOpen: false,
    });
  },
}));

/**
 * 血統書データの型ガード
 */
export function isPedigreeData(
  data: ExtractedPedigreeData | ExtractedMicrochipData | ExtractedGeneticTestData | ExtractedUnifiedData
): data is ExtractedPedigreeData {
  return 'officialName' in data || 'breed' in data || 'jkcRegistrationNumber' in data || 'ancestors' in data;
}

/**
 * マイクロチップデータの型ガード
 */
export function isMicrochipData(
  data: ExtractedPedigreeData | ExtractedMicrochipData | ExtractedGeneticTestData | ExtractedUnifiedData
): data is ExtractedMicrochipData {
  return 'microchipNumber' in data && !('testResults' in data) && !('officialName' in data || 'breed' in data);
}

/**
 * 遺伝子検査データの型ガード
 */
export function isGeneticTestData(
  data: ExtractedPedigreeData | ExtractedMicrochipData | ExtractedGeneticTestData | ExtractedUnifiedData
): data is ExtractedGeneticTestData {
  return 'testResults' in data && !('officialName' in data || 'breed' in data);
}

/**
 * 統合データの型ガード（血統書+マイクロチップ+遺伝子検査のマージデータ）
 */
export function isUnifiedData(
  data: ExtractedPedigreeData | ExtractedMicrochipData | ExtractedGeneticTestData | ExtractedUnifiedData
): data is ExtractedUnifiedData {
  return isPedigreeData(data) && (
    'microchipNumber' in data ||
    'geneticTestResults' in data ||
    'microchipRegistrationDate' in data ||
    'geneticTestDate' in data
  );
}
